import React, { useState, useEffect, useMemo } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { getTeamLogoUrl } from '../utils/formatters';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const getStatFieldForMarket = (marketKey, sport) => {
  if (sport === 'nhl') {
    const nhlMap = {
      'player_goals': 'goals', 'player_assists': 'assists', 'player_points': 'points',
      'player_shots_on_goal': 'shotsOnGoal', 'player_saves': 'saves', 'player_total_saves': 'saves',
      'player_blocked_shots': 'blockedShots',
      'goals': 'goals', 'assists': 'assists', 'points': 'points',
      'shots_on_goal': 'shotsOnGoal', 'shots': 'shotsOnGoal', 'saves': 'saves',
      'total_saves': 'saves', 'blocked': 'blockedShots',
    };
    return nhlMap[marketKey] || null;
  }
  if (sport === 'nfl') {
    const nflMap = {
      'player_pass_yds': 'passingYards',
      'player_pass_tds': 'passingTouchdowns',
      'player_pass_attempts': 'passingAttempts',
      'player_pass_completions': 'passingCompletions',
      'player_pass_interceptions': 'passingInterceptions',
      'player_rush_yds': 'rushingYards',
      'player_rush_attempts': 'rushingAttempts',
      'player_rush_tds': 'rushingTouchdowns',
      'player_rush_longest': 'longRushing',
      'player_receptions': 'receptions',
      'player_reception_yds': 'receivingYards',
      'player_reception_tds': 'receivingTouchdowns',
      'player_reception_longest': 'longReception',
      // Kicking.
      'player_field_goals': 'fieldGoalsMade',
      // Composite TD markets handled by comboStatValue below.
      'player_anytime_td': '__anyTd',
      'player_tds_over': '__totalTd',
      // Combo yardage: rush yards + reception yards.
      'player_rush_reception_yds': '__rushRecYds',
    };
    return nflMap[marketKey] || null;
  }
  const marketToStat = {
    'player_points': 'pts', 'player_assists': 'ast', 'player_rebounds': 'reb',
    'player_steals': 'stl', 'player_blocks': 'blk', 'player_turnovers': 'turnover',
    'player_threes': 'fg3m', 'player_first_basket': 'first_fg',
    'player_first_team_basket': 'first_team_fg',
    'points': 'pts', 'assists': 'ast', 'rebounds': 'reb',
    'steals': 'stl', 'blocks': 'blk', 'turnovers': 'turnover',
    'threes': 'fg3m', 'first_basket': 'first_fg', 'first_team_basket': 'first_team_fg',
  };
  return marketToStat[marketKey] || null;
};

// NFL helpers — total touchdowns (sum of rush/receive/pass) and anytime-TD
// binary indicator derived from the sum.
const nflTotalTds = (game) => (
  (game.rushingTouchdowns || 0) +
  (game.receivingTouchdowns || 0) +
  (game.passingTouchdowns || 0)
);
const nflAnytimeTd = (game) => nflTotalTds(game) > 0 ? 1 : 0;
const nflRushRecYds = (game) => (game.rushingYards || 0) + (game.receivingYards || 0);

// Y-axis step size per sport/market. Yardage markets use coarse (10/25 yard)
// gridlines so the ticks don't become an unreadable wall; integer markets
// (TDs, receptions, attempts, NHL/NBA stats) stay at 1.
const getYStepSize = (sport, marketKey) => {
  if (sport === 'nfl') {
    if (marketKey === 'player_pass_yds') return 25;
    if (marketKey === 'player_rush_yds' || marketKey === 'player_reception_yds' || marketKey === 'player_rush_reception_yds') return 10;
    if (marketKey === 'player_rush_longest' || marketKey === 'player_reception_longest') return 10;
  }
  return 1;
};

// Pick a y-axis top that's always strictly above the tallest bar + the
// reference line, rounded up to the next multiple of `step` so the top
// gridline is a clean number. Scales the target by ~10% so bars always
// have breathing room above them instead of touching the top of the
// chart area.
const computeYSuggestedMax = (maxVal, step, floor) => {
  const target = Math.max(maxVal, floor || 0);
  if (!Number.isFinite(target) || target <= 0) return floor || step;
  // 10% padding + half-step bump, then round up to the next clean step
  // boundary so the top gridline is a round number.
  const padded = target * 1.1 + step * 0.5;
  const rounded = Math.ceil(padded / step) * step;
  // Guarantee at least one full step of headroom above the tallest bar.
  return rounded > target ? rounded : rounded + step;
};

const getComboStatValue = (game, marketKey) => {
  if (marketKey === 'player_points_rebounds_assists' || marketKey === 'points_rebounds_assists' || marketKey === 'pra') {
    return (game.pts || 0) + (game.reb || 0) + (game.ast || 0);
  }
  if (marketKey === 'player_points_rebounds' || marketKey === 'points_rebounds') {
    return (game.pts || 0) + (game.reb || 0);
  }
  if (marketKey === 'player_points_assists' || marketKey === 'points_assists') {
    return (game.pts || 0) + (game.ast || 0);
  }
  if (marketKey === 'player_rebounds_assists' || marketKey === 'rebounds_assists') {
    return (game.reb || 0) + (game.ast || 0);
  }
  return null;
};

const countStats10Plus = (game) => {
  let count = 0;
  if ((game.pts || 0) >= 10) count++;
  if ((game.reb || 0) >= 10) count++;
  if ((game.ast || 0) >= 10) count++;
  if ((game.stl || 0) >= 10) count++;
  if ((game.blk || 0) >= 10) count++;
  return count;
};

const isDoubleDouble = (game) => countStats10Plus(game) >= 2 ? 1 : 0;
const isTripleDouble = (game) => countStats10Plus(game) >= 3 ? 1 : 0;

const getMarketDisplayName = (marketKey) => {
  const map = {
    'player_points': 'POINTS', 'player_assists': 'ASSISTS', 'player_rebounds': 'REBOUNDS',
    'player_steals': 'STEALS', 'player_blocks': 'BLOCKS', 'player_turnovers': 'TURNOVERS',
    'player_threes': 'THREES', 'player_first_basket': 'FIRST BASKET',
    'player_first_team_basket': 'FIRST TEAM BASKET',
    'player_double_double': 'DOUBLE DOUBLE', 'player_triple_double': 'TRIPLE DOUBLE',
    'player_points_rebounds_assists': 'PRA', 'player_points_rebounds': 'P+R',
    'player_points_assists': 'P+A', 'player_rebounds_assists': 'R+A',
    'points': 'POINTS', 'assists': 'ASSISTS', 'rebounds': 'REBOUNDS',
    'steals': 'STEALS', 'blocks': 'BLOCKS', 'turnovers': 'TURNOVERS',
    'threes': 'THREES', 'first_basket': 'FIRST BASKET', 'first_team_basket': 'FIRST TEAM BASKET',
    'double_double': 'DOUBLE DOUBLE', 'triple_double': 'TRIPLE DOUBLE',
    'pra': 'PRA', 'points_rebounds': 'P+R', 'points_assists': 'P+A', 'rebounds_assists': 'R+A',
  };
  return map[marketKey] || marketKey.replace('player_', '').replace(/_/g, ' ').toUpperCase();
};

const getAvgKey = (marketKey) => {
  const map = {
    'player_points': 'ppg', 'points': 'ppg',
    'player_assists': 'apg', 'assists': 'apg',
    'player_rebounds': 'rpg', 'rebounds': 'rpg',
    'player_steals': 'spg', 'steals': 'spg',
    'player_blocks': 'bpg', 'blocks': 'bpg',
    'player_threes': 'fg3m', 'threes': 'fg3m',
    'player_turnovers': 'turnover', 'turnovers': 'turnover',
    'player_points_rebounds_assists': 'pra', 'pra': 'pra',
    'player_points_rebounds': 'pr', 'points_rebounds': 'pr',
    'player_points_assists': 'pa', 'points_assists': 'pa',
    'player_rebounds_assists': 'ra', 'rebounds_assists': 'ra',
    // NHL
    'player_goals': 'gpg', 'goals': 'gpg',
    'player_shots_on_goal': 'sopg', 'shots_on_goal': 'sopg',
    'player_blocked_shots': 'bspg', 'blocked': 'bspg',
    'player_total_saves': 'svpg', 'player_saves': 'svpg', 'total_saves': 'svpg',
    // NFL
    'player_pass_yds': 'payds',
    'player_pass_tds': 'patds',
    'player_pass_attempts': 'patt',
    'player_pass_completions': 'pacomp',
    'player_pass_interceptions': 'paint',
    'player_rush_yds': 'ruyds',
    'player_rush_attempts': 'ruatt',
    'player_rush_tds': 'rutds',
    'player_rush_longest': 'rulong',
    'player_receptions': 'rec',
    'player_reception_yds': 'reyds',
    'player_reception_tds': 'retds',
    'player_reception_longest': 'relong',
    'player_rush_reception_yds': 'rureyds',
    'player_field_goals': 'fgpg',
    'player_tds_over': 'tdpg',
  };
  return map[marketKey] || null;
};

// NBA / NHL seasons cross calendar years (e.g. 2025-26), so their current
// season year is the calendar year the season started in.
const getDefaultStatsYear = (sport) => {
  const now = new Date();
  const y = now.getFullYear();
  if (sport === 'nba' || sport === 'nhl') {
    // Roll over on Aug 1 so preseason (late Sep) already reflects the
    // upcoming season instead of last year's finished one.
    return String(now.getMonth() >= 7 ? y : y - 1);
  }
  return String(y);
};

// Module-level cache of preloaded HTMLImageElement by logo URL so each render
// doesn't re-create Image objects (and so the plugin can read a complete img
// synchronously once the browser has decoded it).
const logoImageCache = new Map();
const getLogoImage = (url) => {
  if (!url) return null;
  if (logoImageCache.has(url)) return logoImageCache.get(url);
  const img = new window.Image();
  img.src = url;
  logoImageCache.set(url, img);
  return img;
};

const PlayerStatsChart = ({ playerId, sport, marketType, playerTeam, awayTeam, homeTeam, lineValue: propLineValue }) => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDNPs, setShowDNPs] = useState(false);
  const [showMinutes, setShowMinutes] = useState(true);
  const [showVsOpponent, setShowVsOpponent] = useState(false);
  const [statsYear, setStatsYear] = useState(() => getDefaultStatsYear(sport));
  // Bumped when a preloaded opponent logo finishes loading so React
  // re-renders the Bar component, which triggers Chart.js to redraw and the
  // teamLogoPlugin to pick up the now-decoded image.
  const [logoLoadTick, setLogoLoadTick] = useState(0);

  const isSupportedSport = sport === 'nba' || sport === 'wnba' || sport === 'nhl' || sport === 'nfl';

  useEffect(() => {
    if (!playerId || !isSupportedSport) return;
    let cancelled = false;

    // Null out stale data immediately so the chart unmounts the moment the
    // user flips Current/Last. Without this, there's a render where
    // statsYear is new but statsData still reflects the old season — the
    // chart momentarily reconciles with mismatched state and Chart.js
    // trips over a tooltip active element whose dataset metadata was
    // replaced ("Cannot read properties of null (reading 'getParsed')").
    setStatsData(null);
    setError(null);

    const fetchStats = async () => {
      setLoading(true);
      try {
        // Different callers pass playerId with or without a sport prefix
        // (e.g. "nfl_4594449" vs "4594449"). The /api/stats endpoint wants
        // the bare id, so strip any `<sport>_` prefix defensively.
        const cleanId = String(playerId).replace(/^[a-z]+_/i, '');
        const response = await fetch(`/api/stats/${sport}/${statsYear}/${cleanId}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) setStatsData(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setStatsData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStats();
    return () => { cancelled = true; };
  }, [playerId, sport, isSupportedSport, statsYear]);

  // Today's opponent — derived from the away/home props the caller passes
  // in alongside playerTeam. Needed so the "vs Opp" toggle can filter the
  // game log to only prior games against the same team.
  const opponentTeam = useMemo(() => {
    if (!playerTeam || !awayTeam || !homeTeam) return null;
    return playerTeam === homeTeam ? awayTeam : homeTeam;
  }, [playerTeam, awayTeam, homeTeam]);

  const chartResult = useMemo(() => {
    if (!statsData || !statsData.gameLog || statsData.gameLog.length === 0) return null;

    const selectedMarketType = marketType || 'player_points';
    const statField = getStatFieldForMarket(selectedMarketType, sport);
    const isNHL = sport === 'nhl';
    const isNFL = sport === 'nfl';
    const isGoalsMarket = isNHL && (selectedMarketType === 'goals' || selectedMarketType === 'player_goals');
    const isComboMarket = !isNFL && (selectedMarketType === 'pra' ||
      (selectedMarketType.includes('_') &&
        (selectedMarketType.includes('points_rebounds') ||
         selectedMarketType.includes('points_assists') ||
         selectedMarketType.includes('rebounds_assists'))));
    const isDoubleDoubleMarket = selectedMarketType === 'double_double' || selectedMarketType === 'player_double_double';
    const isTripleDoubleMarket = selectedMarketType === 'triple_double' || selectedMarketType === 'player_triple_double';
    const isFirstBasketMarket = selectedMarketType === 'first_basket' || selectedMarketType === 'player_first_basket';
    const isFirstTeamBasketMarket = selectedMarketType === 'first_team_basket' || selectedMarketType === 'player_first_team_basket';
    // NFL binary market: anytime touchdown (player scored any TD this game).
    const isAnytimeTdMarket = isNFL && (selectedMarketType === 'player_anytime_td' || selectedMarketType === 'anytime_td');
    const isTotalTdMarket = isNFL && (selectedMarketType === 'player_tds_over' || selectedMarketType === 'total_tds');
    const isRushRecYdsMarket = isNFL && selectedMarketType === 'player_rush_reception_yds';
    const isBinaryMarket = isDoubleDoubleMarket || isTripleDoubleMarket || isFirstBasketMarket || isFirstTeamBasketMarket || isAnytimeTdMarket;

    if (!statField && !isComboMarket && !isDoubleDoubleMarket && !isTripleDoubleMarket && !isFirstBasketMarket && !isFirstTeamBasketMarket && !isAnytimeTdMarket && !isTotalTdMarket && !isRushRecYdsMarket) return null;

    // Resolve the per-game numeric value for the active market.
    const extractStat = (game) => {
      if (isAnytimeTdMarket) return nflAnytimeTd(game);
      if (isTotalTdMarket) return nflTotalTds(game);
      if (isRushRecYdsMarket) return nflRushRecYds(game);
      return game[statField] || 0;
    };

    // DNP detection — NHL/NBA have minutes/TOI; NFL has no minutes concept
    // in the game log, so treat a game with zero across all tracked stats
    // as a DNP (inactive / didn't play).
    const isDNP = (game) => {
      if (isNHL) {
        const toi = game.timeOnIce || '0:00';
        return toi === '0:00' || toi === '00:00' || !toi;
      }
      if (isNFL) {
        return (game.passingYards || 0) === 0 &&
               (game.rushingYards || 0) === 0 &&
               (game.receivingYards || 0) === 0 &&
               (game.receptions || 0) === 0 &&
               (game.passingAttempts || 0) === 0 &&
               (game.rushingAttempts || 0) === 0 &&
               (game.fieldGoalAttempts || 0) === 0 &&
               (game.extraPointAttempts || 0) === 0 &&
               nflTotalTds(game) === 0;
      }
      const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
      return minutes === 0;
    };

    // Opponent abbreviation for a given gameLog row, derived the same way
    // the on-canvas @/vs row derives it below. Used by the "vs Opp" filter.
    const oppAbbrevForGame = (game) => {
      if (isNHL) {
        const isHome = game.team === game.homeTeam;
        return isHome ? game.awayTeam : game.homeTeam;
      }
      if (isNFL) {
        const isHome = game.team === (game.homeTeamAbbrev || game.homeTeam);
        return isHome ? (game.awayTeamAbbrev || game.awayTeam) : (game.homeTeamAbbrev || game.homeTeam);
      }
      return game.teamId === game.homeTeamId
        ? game.awayTeamAbbrev
        : game.homeTeamAbbrev;
    };

    const sortedGames = [...statsData.gameLog]
      .filter(game => {
        if (!showDNPs && isDNP(game)) return false;
        if (showVsOpponent && opponentTeam) {
          const opp = oppAbbrevForGame(game);
          if (!opp || opp.toUpperCase() !== String(opponentTeam).toUpperCase()) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.gameDate) - new Date(a.gameDate));

    if (sortedGames.length === 0) return null;

    // Played games for averages (always exclude DNPs for avg calc)
    const playedGames = statsData.gameLog.filter(game => !isDNP(game));
    const gamesPlayedCount = playedGames.length;

    // X-axis label shows the game date only. The opponent's @/vs prefix +
    // team logo live in a dedicated row underneath the chart, built from
    // `opponents` below — so we don't duplicate the abbreviation here.
    const gameLabels = sortedGames.map(game => {
      const date = new Date(game.gameDate + 'T12:00:00-05:00');
      return `${date.getMonth() + 1}/${date.getDate()}`;
    });

    // Opponent metadata per game, mirrored under the chart as team logos.
    const gameOpponents = sortedGames.map(game => {
      if (isNHL) {
        const isHome = game.team === game.homeTeam;
        return { abbrev: isHome ? game.awayTeam : game.homeTeam, isHome };
      }
      if (isNFL) {
        const isHome = game.team === (game.homeTeamAbbrev || game.homeTeam);
        return { abbrev: isHome ? (game.awayTeamAbbrev || game.awayTeam) : (game.homeTeamAbbrev || game.homeTeam), isHome };
      }
      const isHome = game.teamId === game.homeTeamId;
      return { abbrev: isHome ? game.awayTeamAbbrev : game.homeTeamAbbrev, isHome };
    });

    // Score differential (player team final - opponent final) per game.
    // NBA stats use homeTeamScore / awayTeamScore; MLB-ish payloads use
    // homeScore / awayScore. Null when neither pair is present, so sports
    // whose game log doesn't carry a final score degrade gracefully.
    const gameScoreDiffs = sortedGames.map((game, idx) => {
      const h = Number(game.homeTeamScore ?? game.homeScore);
      const a = Number(game.awayTeamScore ?? game.awayScore);
      if (!Number.isFinite(h) || !Number.isFinite(a)) return null;
      const isHome = gameOpponents[idx]?.isHome;
      if (isHome == null) return null;
      return isHome ? h - a : a - h;
    });

    const gameData = sortedGames.map(game => {
      if (isDNP(game)) {
        if (isBinaryMarket) return null;
        return isNHL ? 0.25 : 0;
      }
      if (isDoubleDoubleMarket) return isDoubleDouble(game);
      if (isTripleDoubleMarket) return isTripleDouble(game);
      if (isFirstBasketMarket) return game.first_fg ? 1 : 0;
      if (isFirstTeamBasketMarket) return game.first_team_fg ? 1 : 0;
      if (isAnytimeTdMarket) return nflAnytimeTd(game);
      if (isTotalTdMarket) {
        const val = nflTotalTds(game);
        if (val === 0) return 0.4;
        return val;
      }
      if (isComboMarket) {
        const val = getComboStatValue(game, selectedMarketType);
        if (val === 0) return isNHL ? 0.25 : 0.4;
        return val;
      }
      const val = extractStat(game);
      if (val === 0) return isNHL ? 0.25 : 0.4;
      return val;
    });

    // Compute a summary "Avg" bar to prepend at the far left of the chart.
    // We use it as a visual anchor so users can eyeball each game's bar
    // against the season average without scanning the dashed line across
    // the whole chart. Skip on binary markets (Yes/No has no meaningful
    // average as a bar height).
    const showAvgBar = !isBinaryMarket;
    const gameRealValues = (isNHL || isNFL)
      ? sortedGames.map(extractStat)
      : gameData;
    const avgRawValue = gameRealValues.length > 0
      ? gameRealValues.reduce((s, v) => s + (v || 0), 0) / gameRealValues.length
      : 0;
    // Match the same "min visible bar" convention used for per-game bars
    // (0.4 or 0.25) so a 0 avg still shows a sliver.
    const avgDisplayValue = avgRawValue === 0
      ? (isNHL ? 0.25 : 0.4)
      : avgRawValue;

    const labels = showAvgBar ? ['Avg', ...gameLabels] : gameLabels;
    const data = showAvgBar ? [avgDisplayValue, ...gameData] : gameData;
    const opponents = showAvgBar
      ? [{ abbrev: null, isHome: null }, ...gameOpponents]
      : gameOpponents;
    const scoreDiffs = showAvgBar ? [null, ...gameScoreDiffs] : gameScoreDiffs;

    const lineVal = propLineValue || null;

    const chartData = isBinaryMarket ? data.map(val => {
      if (val === null) return null;
      // NBA binary markets (first basket, DD, TD) all use the same small-No
      // treatment so the Yes bars read as the signal. NFL anytime TD keeps
      // the taller No bar.
      if (isFirstBasketMarket || isFirstTeamBasketMarket || isDoubleDoubleMarket || isTripleDoubleMarket) {
        return val === 1 ? 2 : 0.25;
      }
      return val === 1 ? 2 : 1;
    }) : data;

    const datasets = [];

    // Neutral color for the summary "Avg" bar so it doesn't inherit the
    // green/red hit-or-miss coloring used for per-game bars.
    const AVG_BAR_BG = 'rgba(96, 165, 250, 0.8)'; // blue-400
    const AVG_BAR_BORDER = 'rgb(96, 165, 250)';

    // For NHL goals market, add shots on goal as background bars.
    // Prepend the season shots-per-game average so it sits behind the Avg
    // goals bar at index 0, matching the per-game shots-behind-goals overlay.
    if (isNHL && isGoalsMarket) {
      const perGameShots = sortedGames.map(game => {
        const shots = game.shotsOnGoal || 0;
        if (shots === 0) return 0.25;
        return shots;
      });
      const rawShots = sortedGames.map(g => g.shotsOnGoal || 0);
      const avgShots = rawShots.length
        ? rawShots.reduce((s, v) => s + v, 0) / rawShots.length
        : 0;
      const shotsAvgDisplay = avgShots === 0 ? 0.25 : avgShots;
      const shotsData = showAvgBar ? [shotsAvgDisplay, ...perGameShots] : perGameShots;
      datasets.push({
        type: 'bar',
        label: 'Shots on Goal',
        data: shotsData,
        backgroundColor: 'rgba(148, 163, 184, 0.4)',
        borderColor: 'rgba(148, 163, 184, 0.6)',
        borderWidth: 1,
        // Same barThickness as the goals dataset so widths match, plus
        // grouped:false so Chart.js draws this dataset at the same x
        // coordinate as goals (background overlay) instead of adjacent.
        barThickness: 30,
        grouped: false,
        order: 3
      });
    }

    // (NFL team-offense-snaps overlay lives on the Snaps sub-chart below —
    // see the Snaps/Minutes/Shifts sub-chart render in the JSX.)

    datasets.push({
      type: 'bar',
      label: isNHL ? selectedMarketType.replace('player_', '').replace(/_/g, ' ').toUpperCase() : getMarketDisplayName(selectedMarketType),
      data: chartData,
      backgroundColor: data.map((val, idx) => {
        if (showAvgBar && idx === 0) return AVG_BAR_BG; // avg column
        if (isBinaryMarket) return val === 1 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
        const gameIdx = showAvgBar ? idx - 1 : idx;
        const actualVal = (isNHL || isNFL) ? extractStat(sortedGames[gameIdx]) : val;
        return lineVal && actualVal >= lineVal ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
      }),
      borderColor: data.map((val, idx) => {
        if (showAvgBar && idx === 0) return AVG_BAR_BORDER;
        if (isBinaryMarket) return val === 1 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)';
        const gameIdx = showAvgBar ? idx - 1 : idx;
        const actualVal = (isNHL || isNFL) ? extractStat(sortedGames[gameIdx]) : val;
        return lineVal && actualVal >= lineVal ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)';
      }),
      borderWidth: 1,
      // Fixed bar width so single-game charts don't stretch to a giant bar.
      barThickness: 30,
      order: 2
    });

    // NHL goals market skips line/avg lines (matching modal behavior)
    if (lineVal !== null && !isBinaryMarket && !(isNHL && isGoalsMarket)) {
      datasets.push({
        type: 'line',
        label: `Line (${lineVal})`,
        data: Array(data.length).fill(lineVal),
        borderColor: 'rgba(59, 130, 246, 0.9)',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        fill: false,
        order: 1
      });
    }

    if (!isBinaryMarket && data.length > 0 && !(isNHL && isGoalsMarket)) {
      const realData = (isNHL || isNFL) ? sortedGames.map(extractStat) : data;
      const avgValue = realData.reduce((sum, val) => sum + val, 0) / realData.length;
      datasets.push({
        type: 'line',
        label: `Avg (${avgValue.toFixed(1)})`,
        data: Array(data.length).fill(avgValue),
        borderColor: 'rgba(251, 191, 36, 0.9)',
        borderWidth: 2,
        borderDash: [3, 3],
        pointRadius: 0,
        fill: false,
        order: 1
      });
    }

    // Calculate custom averages (two decimals across the board) and season
    // totals. Totals feed the Avg-bar tooltip; averages feed both the tooltip
    // and the header/legend text.
    const customAverages = {};
    const seasonTotals = {};
    const sumField = (field) => playedGames.reduce((s, g) => s + (g[field] || 0), 0);
    if (gamesPlayedCount > 0) {
      if (isNHL) {
        seasonTotals.goals = sumField('goals');
        seasonTotals.assists = sumField('assists');
        seasonTotals.points = sumField('points');
        seasonTotals.shotsOnGoal = sumField('shotsOnGoal');
        seasonTotals.blockedShots = sumField('blockedShots');
        seasonTotals.saves = sumField('saves');
        customAverages.gpg = (seasonTotals.goals / gamesPlayedCount).toFixed(1);
        customAverages.apg = (seasonTotals.assists / gamesPlayedCount).toFixed(1);
        customAverages.ppg = (seasonTotals.points / gamesPlayedCount).toFixed(1);
        customAverages.sopg = (seasonTotals.shotsOnGoal / gamesPlayedCount).toFixed(1);
        customAverages.bspg = (seasonTotals.blockedShots / gamesPlayedCount).toFixed(1);
        customAverages.svpg = (seasonTotals.saves / gamesPlayedCount).toFixed(1);
      } else if (isNFL) {
        seasonTotals.passingYards = sumField('passingYards');
        seasonTotals.passingTouchdowns = sumField('passingTouchdowns');
        seasonTotals.passingAttempts = sumField('passingAttempts');
        seasonTotals.passingCompletions = sumField('passingCompletions');
        seasonTotals.passingInterceptions = sumField('passingInterceptions');
        seasonTotals.rushingYards = sumField('rushingYards');
        seasonTotals.rushingAttempts = sumField('rushingAttempts');
        seasonTotals.rushingTouchdowns = sumField('rushingTouchdowns');
        seasonTotals.receptions = sumField('receptions');
        seasonTotals.receivingYards = sumField('receivingYards');
        seasonTotals.receivingTouchdowns = sumField('receivingTouchdowns');
        seasonTotals.totalTds = playedGames.reduce((s, g) => s + nflTotalTds(g), 0);
        customAverages.payds = (seasonTotals.passingYards / gamesPlayedCount).toFixed(1);
        customAverages.patds = (seasonTotals.passingTouchdowns / gamesPlayedCount).toFixed(1);
        customAverages.patt = (seasonTotals.passingAttempts / gamesPlayedCount).toFixed(1);
        customAverages.pacomp = (seasonTotals.passingCompletions / gamesPlayedCount).toFixed(1);
        customAverages.paint = (seasonTotals.passingInterceptions / gamesPlayedCount).toFixed(1);
        customAverages.ruyds = (seasonTotals.rushingYards / gamesPlayedCount).toFixed(1);
        customAverages.ruatt = (seasonTotals.rushingAttempts / gamesPlayedCount).toFixed(1);
        customAverages.rutds = (seasonTotals.rushingTouchdowns / gamesPlayedCount).toFixed(1);
        customAverages.rec = (seasonTotals.receptions / gamesPlayedCount).toFixed(1);
        customAverages.reyds = (seasonTotals.receivingYards / gamesPlayedCount).toFixed(1);
        customAverages.retds = (seasonTotals.receivingTouchdowns / gamesPlayedCount).toFixed(1);
        customAverages.tdpg = (seasonTotals.totalTds / gamesPlayedCount).toFixed(1);
      } else {
        seasonTotals.pts = sumField('pts');
        seasonTotals.reb = sumField('reb');
        seasonTotals.ast = sumField('ast');
        seasonTotals.stl = sumField('stl');
        seasonTotals.blk = sumField('blk');
        seasonTotals.fg3m = sumField('fg3m');
        seasonTotals.turnover = sumField('turnover');
        customAverages.ppg = (seasonTotals.pts / gamesPlayedCount).toFixed(1);
        customAverages.rpg = (seasonTotals.reb / gamesPlayedCount).toFixed(1);
        customAverages.apg = (seasonTotals.ast / gamesPlayedCount).toFixed(1);
        customAverages.spg = (seasonTotals.stl / gamesPlayedCount).toFixed(1);
        customAverages.bpg = (seasonTotals.blk / gamesPlayedCount).toFixed(1);
        customAverages.fg3m = (seasonTotals.fg3m / gamesPlayedCount).toFixed(1);
        customAverages.turnover = (seasonTotals.turnover / gamesPlayedCount).toFixed(1);
        customAverages.pra = ((seasonTotals.pts + seasonTotals.reb + seasonTotals.ast) / gamesPlayedCount).toFixed(1);
        customAverages.pr = ((seasonTotals.pts + seasonTotals.reb) / gamesPlayedCount).toFixed(1);
        customAverages.pa = ((seasonTotals.pts + seasonTotals.ast) / gamesPlayedCount).toFixed(1);
        customAverages.ra = ((seasonTotals.reb + seasonTotals.ast) / gamesPlayedCount).toFixed(1);
        const ddCount = playedGames.filter(g => isDoubleDouble(g) === 1).length;
        const tdCount = playedGames.filter(g => isTripleDouble(g) === 1).length;
        customAverages.ddHits = ddCount;
        customAverages.ddRate = ((ddCount / gamesPlayedCount) * 100).toFixed(0);
        customAverages.tdHits = tdCount;
        customAverages.tdRate = ((tdCount / gamesPlayedCount) * 100).toFixed(0);
        const fbCount = playedGames.filter(g => g.first_fg === true).length;
        customAverages.fbHits = fbCount;
        customAverages.fbRate = ((fbCount / gamesPlayedCount) * 100).toFixed(0);
        const ftbCount = playedGames.filter(g => g.first_team_fg === true).length;
        customAverages.ftbHits = ftbCount;
        customAverages.ftbRate = ((ftbCount / gamesPlayedCount) * 100).toFixed(0);
      }
    }

    // Minutes/TOI and raw stat data for tooltips. Prepend an entry for the
    // Avg column (index 0) so tooltip indices match the bar indices.
    const perGameMinutes = isNHL
      ? sortedGames.map(game => game.timeOnIce || '0:00')
      : sortedGames.map(game => {
        const min = typeof game.min === 'string' ? parseInt(game.min) : game.min;
        return min || 0;
      });
    const minutesData = showAvgBar ? ['—', ...perGameMinutes] : perGameMinutes;
    const shiftsData = isNHL
      ? (showAvgBar ? ['—', ...sortedGames.map(game => game.shifts || 0)] : sortedGames.map(game => game.shifts || 0))
      : null;
    const shotsData = isNHL
      ? (showAvgBar ? ['—', ...sortedGames.map(game => game.shotsOnGoal || 0)] : sortedGames.map(game => game.shotsOnGoal || 0))
      : null;
    // NFL snap counts — offenseSnaps is the right dimension for the player
    // prop charts we draw (pass/rush/rec stats). Fall back to totalSnaps
    // when offense isn't broken out.
    const snapsData = isNFL
      ? (showAvgBar
          ? ['—', ...sortedGames.map(game => game.offenseSnaps || game.totalSnaps || 0)]
          : sortedGames.map(game => game.offenseSnaps || game.totalSnaps || 0))
      : null;
    // Team-level offensive snaps for the game — used as a background bar
    // behind the player's snaps on the Snaps sub-chart (mirrors the NHL
    // Shots-on-Goal overlay on the main goals chart).
    const teamSnapsData = isNFL
      ? (showAvgBar
          ? ['—', ...sortedGames.map(g => g.teamOffenseSnaps || 0)]
          : sortedGames.map(g => g.teamOffenseSnaps || 0))
      : null;

    const perGameRawStat = sortedGames.map(game => {
      if (isDNP(game)) return (isNHL || isNFL) ? 'DNP' : (isBinaryMarket ? 'DNP' : 0);
      if (isDoubleDoubleMarket) return isDoubleDouble(game) === 1 ? 'Yes' : 'No';
      if (isTripleDoubleMarket) return isTripleDouble(game) === 1 ? 'Yes' : 'No';
      if (isFirstBasketMarket || isFirstTeamBasketMarket) {
        const fieldName = isFirstBasketMarket ? 'first_fg' : 'first_team_fg';
        return game[fieldName] ? 'Yes' : 'No';
      }
      if (isAnytimeTdMarket) return nflAnytimeTd(game) === 1 ? 'Yes' : 'No';
      if (isTotalTdMarket) return nflTotalTds(game);
      if (isComboMarket) return getComboStatValue(game, selectedMarketType);
      return extractStat(game);
    });
    // For the Avg column, show the numeric average (rounded) in the tooltip.
    const rawStatData = showAvgBar
      ? [avgRawValue.toFixed(1), ...perGameRawStat]
      : perGameRawStat;

    // Y-axis sizing — compute from the data so the top gridline is a clean
    // multiple of the step above the tallest bar (incl. shots-on-goal
    // overlay for NHL goals, and the dashed Line reference if set). Binary
    // markets keep their fixed Yes/No range.
    const yStep = getYStepSize(sport, selectedMarketType);
    let yDataMax = 0;
    if (!isBinaryMarket) {
      yDataMax = gameRealValues.reduce((m, v) => (v > m ? v : m), 0);
      if (isNHL && isGoalsMarket) {
        const shotsPeak = sortedGames.reduce((m, g) => {
          const s = g.shotsOnGoal || 0;
          return s > m ? s : m;
        }, 0);
        if (shotsPeak > yDataMax) yDataMax = shotsPeak;
      }
      if (lineVal != null && lineVal > yDataMax) yDataMax = lineVal;
    }
    const yAxisConfig = {
      stepSize: yStep,
      suggestedMax: isBinaryMarket ? undefined : computeYSuggestedMax(yDataMax, yStep, yStep * 2),
    };

    // Minute-by-minute on-court averages across the filtered games.
    // Each timeline char is `tickSeconds` (usually 10) wide, so a regulation
    // minute is 6 ticks. For each minute 1..48 we tally seconds on court per
    // game and average across games that contributed a timeline for that
    // minute. Only meaningful for NBA right now (that's the only endpoint
    // emitting `onCourt`), but we don't gate here — `hasOnCourtData` falls
    // out of the raw data and the JSX gates on that + sport.
    const MINUTE_COUNT = 48;
    const minuteSecTotals = new Array(MINUTE_COUNT).fill(0);
    const minuteGameCounts = new Array(MINUTE_COUNT).fill(0);
    sortedGames.forEach(game => {
      const timeline = game.onCourt && typeof game.onCourt.timeline === 'string'
        ? game.onCourt.timeline
        : null;
      if (!timeline) return;
      const tickSec = (game.onCourt && game.onCourt.tickSeconds) || 10;
      const ticksPerMinute = Math.max(1, Math.round(60 / tickSec));
      for (let m = 0; m < MINUTE_COUNT; m++) {
        const start = m * ticksPerMinute;
        if (start >= timeline.length) break;
        const end = Math.min(start + ticksPerMinute, timeline.length);
        let ones = 0;
        for (let i = start; i < end; i++) {
          if (timeline[i] === '1') ones++;
        }
        minuteSecTotals[m] += ones * tickSec;
        minuteGameCounts[m] += 1;
      }
    });
    const minuteAvgSeconds = minuteSecTotals.map(
      (total, i) => (minuteGameCounts[i] > 0 ? total / minuteGameCounts[i] : 0)
    );
    const hasOnCourtData = minuteGameCounts.some(c => c > 0);

    return {
      labels,
      datasets,
      opponents,
      scoreDiffs,
      minutesData,
      shiftsData,
      shotsData,
      snapsData,
      teamSnapsData,
      rawStatData,
      minuteAvgSeconds,
      minuteGameCounts,
      hasOnCourtData,
      // Index of the Avg column — always 0 (first bar) when the Avg bar
      // is shown; -1 when it isn't (binary markets).
      avgColIdx: showAvgBar ? 0 : -1,
      lineValue: lineVal,
      customAverages,
      seasonTotals,
      gamesPlayedCount,
      isBinaryMarket,
      isFirstBasketMarket,
      isFirstTeamBasketMarket,
      isNHL,
      isNFL,
      isGoalsMarket,
      yAxisConfig,
      selectedMarketType,
    };
  }, [statsData, marketType, propLineValue, showDNPs, showVsOpponent, opponentTeam, sport]);

  // Preload opponent team logos once chartResult is ready. Each Image hooks
  // onload → bump logoLoadTick → re-render → chart redraws with the now-
  // decoded image. Cached by URL so toggling seasons / switching players
  // reuses already-loaded images instantly.
  useEffect(() => {
    const opponents = chartResult?.opponents;
    if (!opponents || opponents.length === 0) return;
    opponents.forEach((opp) => {
      if (!opp || !opp.abbrev) return;
      const url = getTeamLogoUrl(opp.abbrev, true, sport);
      if (!url || logoImageCache.has(url)) return;
      const img = new window.Image();
      img.onload = () => setLogoLoadTick((n) => n + 1);
      img.onerror = () => setLogoLoadTick((n) => n + 1);
      img.src = url;
      logoImageCache.set(url, img);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartResult, sport]);

  if (!isSupportedSport) return null;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '12px' }}>
        <Spinner animation="border" size="sm" variant="light" />
      </div>
    );
  }

  if (error) return null;

  // When the player has no stats for the selected year we still want to render
  // the header so the year toggle stays reachable.
  const selectedMarketType = chartResult?.selectedMarketType || marketType || 'player_points';

  // Build header info line - always show avg even without line
  const getHeaderInfo = () => {
    if (!chartResult) return null;
    if (chartResult.isBinaryMarket) {
      const avg = chartResult.customAverages;
      if (!avg) return null;
      if (selectedMarketType === 'double_double' || selectedMarketType === 'player_double_double') {
        return `${avg.ddHits}/${chartResult.gamesPlayedCount} (${avg.ddRate}%)`;
      }
      if (selectedMarketType === 'triple_double' || selectedMarketType === 'player_triple_double') {
        return `${avg.tdHits}/${chartResult.gamesPlayedCount} (${avg.tdRate}%)`;
      }
      if (selectedMarketType === 'first_basket' || selectedMarketType === 'player_first_basket') {
        return `${avg.fbRate}% First Baskets (${avg.fbHits}/${chartResult.gamesPlayedCount})`;
      }
      if (selectedMarketType === 'first_team_basket' || selectedMarketType === 'player_first_team_basket') {
        return `${avg.ftbRate}% First Team Baskets (${avg.ftbHits}/${chartResult.gamesPlayedCount})`;
      }
      return null;
    }
    const avgKey = getAvgKey(selectedMarketType);
    const avgVal = avgKey && chartResult.customAverages ? chartResult.customAverages[avgKey] : null;
    let info = '';
    if (chartResult.lineValue) {
      info = `Line: ${chartResult.lineValue}${avgVal ? ` | Avg: ${avgVal}` : ''}`;
    } else if (avgVal) {
      info = `Avg: ${avgVal}`;
    }
    // Add shots/game for NHL goals market
    if (chartResult.isGoalsMarket && chartResult.customAverages?.sopg) {
      info += `${info ? ' | ' : ''}Shots/G: ${chartResult.customAverages.sopg}`;
    }
    info += ` (${chartResult.gamesPlayedCount} games)`;
    return info.trim();
  };

  // Chart.js plugin: draws the @/vs text and team logo for each category
  // directly on the canvas, positioned at Chart.js's internal bar centers
  // (chart.scales.x.getPixelForValue). This replaces the previous HTML grid
  // row — HTML `1fr` columns don't match Chart.js's category spacing exactly
  // when a few bars are drawn, so logos ended up slightly off-center from
  // their bars. Drawing on-canvas uses the same scale the bars use, so the
  // alignment is pixel-perfect regardless of column count.
  const LOGO_SIZE = 30;           // matches barThickness so logo width == bar width
  // Reserved below-chart space: @/vs text + 30px logo + score-diff row.
  const LOGO_BOTTOM_PAD = 82;
  const teamLogoPlugin = {
    id: 'teamLogosOnCanvas',
    afterDatasetsDraw: (chart) => {
      const opponents = chartResult?.opponents;
      if (!opponents || opponents.length === 0) return;
      const diffs = chartResult?.scoreDiffs;
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yBottom = chart.chartArea?.bottom;
      if (!xScale || yBottom == null) return;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      opponents.forEach((opp, idx) => {
        if (!opp || !opp.abbrev) return;
        const xPx = xScale.getPixelForValue(idx);
        if (!Number.isFinite(xPx)) return;
        // Row 1: @/vs text, just under the x-axis labels.
        const textY = yBottom + 20;
        ctx.fillStyle = '#6b7280';
        ctx.font = '10px sans-serif';
        ctx.fillText(opp.isHome ? 'vs' : '@', xPx, textY);
        // Row 2: logo, centered on the same xPx as the bar above.
        const img = getLogoImage(getTeamLogoUrl(opp.abbrev, true, sport));
        if (img && img.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, xPx - LOGO_SIZE / 2, textY + 12, LOGO_SIZE, LOGO_SIZE);
        }
        // Row 3: signed score differential (+15 green / -8 red), under logo.
        const diff = diffs ? diffs[idx] : null;
        if (diff != null) {
          ctx.font = 'bold 12px sans-serif';
          ctx.fillStyle = diff > 0
            ? 'rgba(34, 197, 94, 0.95)'
            : diff < 0
              ? 'rgba(239, 68, 68, 0.95)'
              : 'rgba(148, 163, 184, 0.9)';
          const text = diff > 0 ? `+${diff}` : `${diff}`;
          ctx.fillText(text, xPx, textY + 12 + LOGO_SIZE + 6);
        }
      });
      ctx.restore();
    },
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // Reserve space below the chart for the on-canvas @/vs text + logo row
    // drawn by teamLogoPlugin.
    layout: {
      padding: { bottom: LOGO_BOTTOM_PAD },
    },
    // Index-mode intersection so hovering at any point along a column fires
    // the tooltip for every dataset at that x — the shots-on-goal dataset
    // returns null in its label callback (see below), so the goals column's
    // full tooltip surfaces whether the cursor is over the goal bar itself
    // or the taller shots background above it.
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        // Legend lives above the chart so the team-logo row below the x-axis
        // can sit directly under the game dates without the Line/Avg legend
        // chips splitting them.
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 10,
          font: { size: 9 },
          color: '#94a3b8',
          filter: (legendItem) => legendItem.text.includes('Line') || legendItem.text.includes('Avg') || legendItem.text.includes('Shots on Goal')
        }
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const label = items[0]?.label;
            return Array.isArray(label) ? label.join(' ') : label || '';
          },
          label: (item) => {
            // Cap any raw-number display at 1 decimal so values don't come
            // through as 72.368 or 60.50 depending on the source.
            const trim = (v) => {
              const n = typeof v === 'number' ? v : parseFloat(v);
              if (!Number.isFinite(n)) return v;
              return Number.isInteger(n) ? String(n) : n.toFixed(1);
            };
            // Both the dashed O/U reference and the dashed Avg reference are
            // Chart.js line datasets. Differentiate them by their stored
            // label prefix so the tooltip doesn't render two identical
            // "Line: X" entries (one for each horizontal line).
            if (item.dataset.type === 'line') {
              const lbl = String(item.dataset.label || '');
              if (lbl.startsWith('Avg')) return `Avg: ${trim(item.raw)}`;
              return `Line: ${trim(item.raw)}`;
            }
            if (item.dataset.label === 'Shots on Goal') {
              return null; // Prevent duplicate tooltip for background bars
            }
            if (item.dataset.label === 'Minutes') {
              return `${item.raw} min`;
            }
            const idx = item.dataIndex;

            // Avg column (idx 0 when showAvgBar) — dump every season total
            // + per-game average so the user gets the full summary from one
            // hover instead of hunting per stat.
            if (idx === chartResult.avgColIdx && chartResult.customAverages && chartResult.seasonTotals) {
              const t = chartResult.seasonTotals;
              const a = chartResult.customAverages;
              const g = chartResult.gamesPlayedCount;
              const lines = [`${g} games played`];
              if (chartResult.isNHL) {
                lines.push(`Goals: ${t.goals} (${a.gpg}/g)`);
                lines.push(`Assists: ${t.assists} (${a.apg}/g)`);
                lines.push(`Points: ${t.points} (${a.ppg}/g)`);
                lines.push(`Shots: ${t.shotsOnGoal} (${a.sopg}/g)`);
                lines.push(`Blocks: ${t.blockedShots} (${a.bspg}/g)`);
                lines.push(`Saves: ${t.saves} (${a.svpg}/g)`);
              } else if (chartResult.isNFL) {
                lines.push(`Pass Yds: ${t.passingYards} (${a.payds}/g)`);
                lines.push(`Pass TDs: ${t.passingTouchdowns} (${a.patds}/g)`);
                lines.push(`Comp/Att: ${t.passingCompletions}/${t.passingAttempts}`);
                lines.push(`INTs: ${t.passingInterceptions} (${a.paint}/g)`);
                lines.push(`Rush Yds: ${t.rushingYards} (${a.ruyds}/g)`);
                lines.push(`Rush Att: ${t.rushingAttempts} (${a.ruatt}/g)`);
                lines.push(`Rush TDs: ${t.rushingTouchdowns} (${a.rutds}/g)`);
                lines.push(`Rec: ${t.receptions} (${a.rec}/g)`);
                lines.push(`Rec Yds: ${t.receivingYards} (${a.reyds}/g)`);
                lines.push(`Rec TDs: ${t.receivingTouchdowns} (${a.retds}/g)`);
                lines.push(`Total TDs: ${t.totalTds} (${a.tdpg}/g)`);
              } else {
                lines.push(`Points: ${t.pts} (${a.ppg}/g)`);
                lines.push(`Rebounds: ${t.reb} (${a.rpg}/g)`);
                lines.push(`Assists: ${t.ast} (${a.apg}/g)`);
                lines.push(`Steals: ${t.stl} (${a.spg}/g)`);
                lines.push(`Blocks: ${t.blk} (${a.bpg}/g)`);
                lines.push(`3PM: ${t.fg3m} (${a.fg3m}/g)`);
                lines.push(`TO: ${t.turnover} (${a.turnover}/g)`);
              }
              return lines;
            }

            const statVal = chartResult.rawStatData?.[idx];
            if (chartResult.isNHL) {
              const toi = chartResult.minutesData?.[idx] || '0:00';
              const shifts = chartResult.shiftsData?.[idx] || 0;
              const lines = [
                `${statVal} ${selectedMarketType.replace('player_', '').replace(/_/g, ' ')}`,
                `TOI: ${toi} | Shifts: ${shifts}`
              ];
              if (chartResult.isGoalsMarket) {
                const shots = chartResult.shotsData?.[idx] || 0;
                const pct = shots > 0 ? ((statVal / shots) * 100).toFixed(0) : 0;
                lines.push(`${shots} shots (${pct}% shooting)`);
              }
              return lines;
            }
            if (chartResult.isNFL) {
              return [`${statVal} ${getMarketDisplayName(selectedMarketType).toLowerCase()}`];
            }
            const minutes = chartResult.minutesData?.[idx] || 0;
            if (chartResult.isBinaryMarket) {
              return [`${statVal}`, `${minutes} min`];
            }
            return [`${statVal} ${getMarketDisplayName(selectedMarketType).toLowerCase()}`, `${minutes} min`];
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          font: { size: 8 },
          autoSkip: false,
          color: '#94a3b8'
        },
        // Match the y grid opacity so both axes read as a proper grid instead
        // of the x lines being nearly invisible.
        grid: { color: 'rgba(148, 163, 184, 0.2)', display: true }
      },
      y: {
        beginAtZero: true,
        max: chartResult?.isBinaryMarket ? 2.5 : undefined,
        // Per-market step + top boundary. Yardage markets (NFL pass yds=25,
        // rush/rec yds=10) use coarse gridlines; integer markets stay at
        // step 1. suggestedMax is always rounded up to one step boundary
        // above the tallest bar so bars never touch the top of the chart.
        suggestedMax: chartResult?.yAxisConfig?.suggestedMax,
        // Hold a floor on the y-axis width. Chart.js auto-fit occasionally
        // collapses to ~0 on remount inside a flex container (seen when the
        // Current/Last toggle rebuilds the chart), which drops the whole
        // tick-label column until the next window resize. 40 px fits NFL
        // yardage labels (e.g. "275", "350") and NHL/NBA single/double-
        // digit ticks alike.
        afterFit: (scale) => {
          if (scale.width < 40) scale.width = 40;
        },
        ticks: {
          stepSize: chartResult?.yAxisConfig?.stepSize ?? 1,
          color: '#94a3b8',
          font: { size: 9 },
          callback: function(value) {
            if (chartResult?.isBinaryMarket) {
              if (value === 2) return 'Yes';
              if (value === 1) return 'No';
              return '';
            }
            // Integer-only labels — accumulation stats are always whole
            // numbers, so hide any half-tick Chart.js draws when the range
            // is tiny.
            return Number.isInteger(value) ? value : '';
          }
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.2)',
          display: true
        }
      }
    }
  };

  const headerInfo = getHeaderInfo();

  // Toggle switch helper
  const Toggle = ({ active, onToggle, label, color }) => (
    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#94a3b8'
    }}>
      <div
        onClick={onToggle}
        style={{
          width: '36px',
          height: '20px',
          borderRadius: '10px',
          backgroundColor: active ? color : '#4b5563',
          position: 'relative',
          transition: 'background-color 0.2s',
          cursor: 'pointer'
        }}
      >
        <div style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          backgroundColor: 'white',
          position: 'absolute',
          top: '2px',
          left: active ? '18px' : '2px',
          transition: 'left 0.2s'
        }} />
      </div>
      {label}
    </label>
  );

  return (
    <div style={{
      marginTop: '12px',
      padding: '10px',
      backgroundColor: '#1a1a2e',
      borderRadius: '8px'
    }}>
      {/* Header matching modal style */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
        <span style={{ margin: 0, color: '#e2e8f0', fontSize: '0.75rem', fontWeight: 600 }}>
          Game Log - {getMarketDisplayName(selectedMarketType)}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {headerInfo && (
            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
              {headerInfo}
            </span>
          )}
          {(sport === 'wnba' || sport === 'nba' || sport === 'nhl' || sport === 'nfl') && (() => {
            const currentYear = getDefaultStatsYear(sport);
            const prevYear = String(parseInt(currentYear, 10) - 1);
            return (
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${statsYear === currentYear ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => setStatsYear(currentYear)}
                style={{ fontSize: '0.65rem', padding: '1px 6px' }}
              >
                Current
              </button>
              <button
                type="button"
                className={`btn ${statsYear === prevYear ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => setStatsYear(prevYear)}
                style={{ fontSize: '0.65rem', padding: '1px 6px' }}
              >
                Last
              </button>
            </div>
            );
          })()}
        </div>
      </div>

      {!chartResult && (
        <div style={{
          height: '250px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6c757d',
          fontSize: '0.9rem',
          border: '1px dashed #334155',
          borderRadius: '6px',
          textAlign: 'center',
          padding: '20px',
        }}>
          No game log data for the {statsYear === getDefaultStatsYear(sport) ? 'current' : 'last'} season.
        </div>
      )}

      {/* Scrollable chart area. Inner wrapper uses `margin: 0 auto` so the
          chart centers when it fits inside the panel, but falls back to a
          left-aligned, scrollable layout when the chart canvas is wider
          than the panel — otherwise flex-centering would push the y-axis
          column into the unscrollable negative-left overflow region. */}
      {chartResult && (
      <div
        className="dark-scrollbar"
        style={{ overflowX: 'auto', overflowY: 'hidden' }}
      >
        {(() => {
          // Bar-width + gap per column + y-axis (32) + right pad (8). Fixed
          // (not min) so a chart with a handful of games doesn't stretch to
          // fill 300 px — bars stay tightly packed at their natural width.
          // Chart data arrays are padded with ghost columns upstream so the
          // grid always covers at least ~MIN_COLUMNS columns.
          const perColumn = 44;
          const chartWidthPx = 32 + chartResult.labels.length * perColumn + 8;
          // Chart height includes the bottom padding we reserved for the
          // on-canvas @/vs + logo row (so the row is drawn inside the canvas,
          // perfectly aligned to each bar's x via chart.scales.x).
          const chartHeightPx = 250 + LOGO_BOTTOM_PAD;
          return (
            // Natural chart width + `margin: 0 auto` for centering. Same
            // behavior across every sport: bars stay at their natural
            // tight spacing (barThickness + per-column gap) regardless of
            // how wide the parent is; chart centers when the parent is
            // wider than the canvas, left-aligns + scrolls when narrower.
            <div style={{ height: `${chartHeightPx}px`, width: `${chartWidthPx}px`, margin: '0 auto' }}>
              <Bar
                // Season + dataset size changes rebuild the chart from
                // scratch to avoid Chart.js's tooltip holding stale `active`
                // references to removed bars (seen as a "Cannot read
                // properties of null (reading 'getParsed')" crash on
                // mouseover right after a Current/Last toggle).
                key={`stats-chart-${selectedMarketType}-${showDNPs}-${showVsOpponent}-${statsYear}-${chartResult.labels.length}-${chartResult.datasets.length}-${logoLoadTick}`}
                data={chartResult}
                options={chartOptions}
                plugins={[teamLogoPlugin]}
              />
            </div>
          );
        })()}
      </div>
      )}

      {/* Legend and toggles */}
      {chartResult && (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '8px',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        {/* Legend */}
        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
          <span style={{ color: 'rgb(34, 197, 94)', marginRight: '12px' }}>■ {
            chartResult.isBinaryMarket
              ? (chartResult.isFirstBasketMarket ? '1st FG'
                : chartResult.isFirstTeamBasketMarket ? '1st Tm FG'
                : 'Yes')
              : 'Over'
          }</span>
          {!chartResult.isFirstBasketMarket && !chartResult.isFirstTeamBasketMarket && (
            <span style={{ color: 'rgb(239, 68, 68)' }}>■ {chartResult.isBinaryMarket ? 'No' : 'Under'}</span>
          )}
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {/* Minutes chart doesn't apply to NFL (game log has no minutes
              concept) — hide the toggle there. */}
          <Toggle
            active={showMinutes}
            onToggle={() => setShowMinutes(!showMinutes)}
            label={sport === 'nhl' ? 'Shifts' : sport === 'nfl' ? 'Snaps' : 'Min'}
            color="#3b82f6"
          />
          <Toggle
            active={showDNPs}
            onToggle={() => setShowDNPs(!showDNPs)}
            label="DNPs"
            color="#f59e0b"
          />
          {/* Vs-opponent filter — only renders when we know today's
              opponent (both awayTeam + homeTeam props were passed). */}
          {opponentTeam && (
            <Toggle
              active={showVsOpponent}
              onToggle={() => setShowVsOpponent(!showVsOpponent)}
              label={`vs ${opponentTeam}`}
              color="#10b981"
            />
          )}
        </div>
      </div>
      )}

      {/* Minutes chart - separate chart below when toggle is on. NFL has no
          minutes stat in the game log, so skip entirely. */}
      {chartResult && showMinutes && (() => {
        // Sport-specific "secondary" sub-chart. NBA/WNBA → minutes played,
        // NHL → shifts, NFL → offensive snap count. All share the same bar
        // style; only data source, label, and y-step differ.
        const useShifts = sport === 'nhl';
        const useSnaps = sport === 'nfl';
        const subTitle = useSnaps ? 'Snaps' : useShifts ? 'Shifts' : 'Minutes Played';
        const subLabel = useSnaps ? 'Snaps' : useShifts ? 'Shifts' : 'Minutes';
        const subUnit = useSnaps ? 'snaps' : useShifts ? 'shifts' : 'min';
        const subData = useSnaps ? chartResult.snapsData
          : useShifts ? chartResult.shiftsData
          : chartResult.minutesData;
        const subStep = useSnaps ? 10 : useShifts ? 5 : 10;
        return (
          <div style={{ marginTop: '16px', marginBottom: '16px', paddingBottom: '8px' }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '8px', textAlign: 'center' }}>
              {subTitle}
            </div>
            <div
              className="dark-scrollbar"
              style={{ overflowX: 'auto', overflowY: 'hidden' }}
            >
              <div style={{
                // Match the main chart: natural width + centered via auto
                // margins so the sub-chart sits directly under the main
                // chart with matching bar positions, independent of parent
                // width.
                height: '140px',
                width: `${32 + chartResult.labels.length * 44 + 8}px`,
                margin: '0 auto',
              }}>
                <Bar
                  // Remount when market or overlay structure changes so
                  // Chart.js' tooltip doesn't hold stale `active` references
                  // across dataset-count changes (e.g. the NFL team-offense
                  // snaps overlay toggling on/off).
                  key={`sub-chart-${selectedMarketType}-${statsYear}-${chartResult.labels.length}-${useSnaps ? (chartResult.teamSnapsData ? 'team' : 'solo') : subLabel}`}
                  data={{
                    labels: chartResult.labels,
                    datasets: [
                      // Player's own snaps/shifts/minutes — rendered on top.
                      {
                        type: 'bar',
                        label: subLabel,
                        data: subData,
                        backgroundColor: 'rgba(100, 116, 139, 0.7)',
                        borderColor: 'rgba(100, 116, 139, 0.9)',
                        borderWidth: 1,
                        barThickness: 30,
                        order: 1,
                      },
                      // NFL-only team-offense-snaps background bar (mirrors
                      // the NHL goals/shots overlay). grouped:false so it
                      // sits at the same x as the player's snaps bar.
                      ...(useSnaps && chartResult.teamSnapsData ? [{
                        type: 'bar',
                        label: 'Team Offense Snaps',
                        data: chartResult.teamSnapsData,
                        backgroundColor: 'rgba(148, 163, 184, 0.35)',
                        borderColor: 'rgba(148, 163, 184, 0.55)',
                        borderWidth: 1,
                        barThickness: 30,
                        grouped: false,
                        order: 2,
                      }] : [])
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    // mode:'index' means hovering any bar in a column
                    // includes every dataset at that x — so both the player
                    // snaps bar and the team-offense-snaps overlay get a
                    // label line, regardless of which one the cursor is on.
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                          title: (items) => {
                            const label = items[0]?.label;
                            return Array.isArray(label) ? label.join(' ') : label || '';
                          },
                          label: (item) => {
                            if (item.dataset.label === 'Team Offense Snaps') {
                              return `Team: ${item.raw} snaps`;
                            }
                            return `Player: ${item.raw} ${subUnit}`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: { display: false },
                      y: {
                        beginAtZero: true,
                        // Match the main chart's y-axis minimum width so
                        // the bars line up vertically between the top chart
                        // and this sub-chart (both canvases share the same
                        // outer wrapper width + y-axis floor, so each bar's
                        // left edge sits at the same x).
                        afterFit: (scale) => {
                          if (scale.width < 40) scale.width = 40;
                        },
                        ticks: { color: '#94a3b8', stepSize: subStep },
                        grid: { color: 'rgba(148, 163, 184, 0.1)' }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Average on-court seconds per minute 1..48 across the filtered games.
          Line chart with dashed vertical dividers at the end of each quarter
          (minutes 12, 24, 36). NBA-only — the endpoint is the only one
          emitting `onCourt` timelines. Sits below the minutes bar chart. */}
      {chartResult && sport === 'nba' && chartResult.hasOnCourtData && (() => {
        const quarterDividersPlugin = {
          id: 'quarterDividers',
          afterDatasetsDraw: (chart) => {
            const { ctx, scales, chartArea } = chart;
            if (!scales?.x || !chartArea) return;
            ctx.save();
            ctx.strokeStyle = 'rgba(148, 163, 184, 0.55)';
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            [11, 23, 35].forEach(idx => {
              const a = scales.x.getPixelForValue(idx);
              const b = scales.x.getPixelForValue(idx + 1);
              const x = (a + b) / 2;
              ctx.beginPath();
              ctx.moveTo(x, chartArea.top);
              ctx.lineTo(x, chartArea.bottom);
              ctx.stroke();
            });
            ctx.restore();
          },
        };
        return (
          <div style={{ marginTop: '12px' }}>
            <div style={{
              fontSize: '0.75rem',
              color: '#94a3b8',
              marginBottom: '6px',
              textAlign: 'center'
            }}>
              Avg Court Time by Game Minute (sec)
            </div>
            <div style={{ height: '140px', width: '100%' }}>
              <Line
                key={`court-min-chart-${statsYear}-${showDNPs}-${showVsOpponent}`}
                data={{
                  labels: Array.from({ length: 48 }, (_, i) => String(i + 1)),
                  datasets: [{
                    label: 'Avg Sec On Court',
                    data: chartResult.minuteAvgSeconds,
                    borderColor: 'rgba(59, 130, 246, 0.95)',
                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    borderWidth: 2,
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    fill: true,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        title: (items) => {
                          const min = parseInt(items[0]?.label, 10);
                          const q = Math.floor((min - 1) / 12) + 1;
                          return `Minute ${min} (Q${q})`;
                        },
                        label: (item) => {
                          const n = typeof item.raw === 'number' ? item.raw : 0;
                          const games = chartResult.minuteGameCounts?.[item.dataIndex] || 0;
                          return [`${n.toFixed(1)} sec avg`, `across ${games} game${games === 1 ? '' : 's'}`];
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      ticks: {
                        color: '#94a3b8',
                        font: { size: 9 },
                        autoSkip: false,
                        callback: function(value, index) {
                          const m = index + 1;
                          if (m === 1 || m === 12 || m === 24 || m === 36 || m === 48) return String(m);
                          return '';
                        }
                      },
                      grid: { display: false }
                    },
                    y: {
                      beginAtZero: true,
                      max: 60,
                      display: false,
                    }
                  }
                }}
                plugins={[quarterDividersPlugin]}
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PlayerStatsChart;
